/**
 * Data figures — render-time original imagery for the Fridge.com data surfaces
 * (item 143 original-imagery leg).
 *
 * Three brand-styled, self-contained figures that emit at request time from
 * server components: no image files, no client JS, no charting library — just
 * inline SVG (charts) and semantic HTML (spec table). Every figure is wrapped
 * in a <figure> with a visible <figcaption> and an accessible aria-label so it
 * reads as owned imagery to users, crawlers, and AI assistants alike.
 *
 * These components are purely presentational: the caller passes already-derived
 * points/bars/rows (see components/seo/data-index-summary.ts), so a figure can
 * never diverge from the numbers rendered as text beside it. Light backgrounds
 * only, Fridge.com palette (#0469f0 blue, #ff9951 orange, gray hairlines).
 */
import type { ReactNode } from 'react'
import { FigureActions } from '@/app/data/_components/figure-actions'
import { ChartHoverLayer } from '@/components/data/chart-hover-layer'
import { FRIDGE_LOGO_ASPECT } from '@/lib/brand-logo-data-uri'

/**
 * The wordmark src the page SERVES — the same pixels as
 * `FRIDGE_LOGO_BLUE_DATA_URI`, from `public/`, referenced by URL instead of
 * inlined. The base64 form is ~53 KB and was serialized once per chart into
 * the HTML **and again** into the RSC flight payload: /data/price-index
 * carried eight copies (~424 KB of one asset) and blew its crawler-eye byte
 * budget on 08/23/2026 — the FRIDGE-179 shape exactly.
 *
 * The data URI still exists and is still REQUIRED — but only at PNG-export
 * time: FigureActions rasterizes the figure by loading the serialized SVG as
 * an <img>, and SVG-as-image loads no external resources, so a URL href would
 * silently drop the wordmark from exported pixels (the owner's 07/20
 * directive is that the REAL logo bakes into exports). The export handler
 * therefore swaps this href for the data URI on the CLONE it serializes —
 * dynamic-imported there, so the 53 KB module never ships with the page.
 */
export const FRIDGE_LOGO_BLUE_PUBLIC_SRC = '/Fridge.com-brand-logos/Fridge.com-blue.png'

// ── Embedded Fridge.com logo (baked into every figure SVG) ──────────────────

/**
 * FridgeWordmark — embeds the REAL Fridge.com logo INTO each figure so the
 * brand travels with the graphic when it is downloaded as a PNG or lifted into
 * someone else's article (owner directive 07/20: "NEVER MAKE UP THE LOGO").
 *
 * The canonical logo is referenced by its same-origin `public/` URL (see
 * FRIDGE_LOGO_BLUE_PUBLIC_SRC above for why the base64 form no longer ships
 * with the page, and how exports still get the real bytes). The
 * `data-fridge-wordmark` attribute is the hook the export handler uses to
 * find and inline it — keep it. `size` is the rendered logo HEIGHT in SVG
 * units; width is derived from the true logo aspect ratio. `anchor` positions
 * the image box horizontally against `x`; `y` is the TOP edge of the logo.
 */
export function FridgeWordmark({
  x,
  y,
  anchor = 'end',
  size = 13,
}: {
  x: number
  y: number
  anchor?: 'start' | 'middle' | 'end'
  size?: number
}) {
  // Round to 2dp: a deterministic string on server and client (no chance of a
  // float-serialization hydration mismatch) and a tidier SVG.
  const round = (n: number) => Math.round(n * 100) / 100
  const h = size
  const w = round(h * FRIDGE_LOGO_ASPECT)
  const left = round(anchor === 'end' ? x - w : anchor === 'middle' ? x - w / 2 : x)
  return (
    <image
      href={FRIDGE_LOGO_BLUE_PUBLIC_SRC}
      data-fridge-wordmark="blue"
      x={left}
      y={y}
      width={w}
      height={h}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    />
  )
}

// ── Shared frame ───────────────────────────────────────────────────────────

interface FigureFrameProps {
  /** Small heading above the graphic. */
  title: string
  /** Visible caption below the graphic (the <figcaption>). */
  caption: ReactNode
  /** Optional uppercase meta shown at the top-right of the header. */
  meta?: string
  /** When set, adds the Download-PNG / Share toolbar (filename fridge-com-<slug>). */
  downloadSlug?: string
  /** Section anchor id for the shareable deep link. */
  anchorId?: string
  children: ReactNode
}

/**
 * Branded card frame for a data figure: white card, gray-200 hairline,
 * rounded-xl, a header row (title + optional meta + actions), the graphic, and
 * a tinted figcaption. Used by every figure in this file for one consistent
 * treatment. When `downloadSlug` is passed the header carries the interactive
 * FigureActions toolbar (Download PNG + Share).
 */
function FigureFrame({ title, caption, meta, downloadSlug, anchorId, children }: FigureFrameProps) {
  return (
    <figure className="m-0 bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Mobile-first: the header STACKS — row 1 the full-width title, row 2
          the toolbar (PNG + share icons) — so the title never collapses to one
          word per line and the icon row never fights it for space. From sm up it
          returns to the inline title-left / toolbar-right layout. Zero
          horizontal overflow at 375/390 (owner spec 07/20). */}
      <div className="px-5 sm:px-6 py-3.5 border-b border-gray-200 flex flex-col gap-y-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
        <h3 className="text-sm sm:text-[15px] font-bold tracking-tight text-gray-900 m-0">
          {title}
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0 sm:justify-end">
          {meta && (
            <span className="hidden md:inline text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 whitespace-nowrap">
              {meta}
            </span>
          )}
          {downloadSlug && (
            <FigureActions slug={downloadSlug} shareTitle={title} anchorId={anchorId} />
          )}
        </div>
      </div>
      <div className="px-4 sm:px-6 py-5">{children}</div>
      <figcaption className="px-5 sm:px-6 py-3 border-t border-gray-100 bg-[#0469f0]/[0.03] text-[13px] text-gray-600 leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  )
}

// ── Line chart ─────────────────────────────────────────────────────────────

export interface TrendChartSeries {
  label: string
  color: string
  points: Array<{ month: string; index: number }>
}

interface DataTrendChartProps {
  series: TrendChartSeries[]
  title: string
  caption: ReactNode
  /** Accessible description of the whole figure; brand rule: end with " | Fridge.com". */
  alt: string
  meta?: string
  /** Reference line (e.g. 100 = base month). */
  baseline?: number
  /** Format a y value for the axis and end labels. Default: 2-dp. */
  yFormat?: (n: number) => string
  /** When set, adds the Download-PNG / Share toolbar (fridge-com-<slug>.png). */
  downloadSlug?: string
  /** Section anchor id for the shareable deep link. */
  anchorId?: string
}

/** 'YYYY-MM' → "May '24" for compact x-axis ticks. */
function shortMonth(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const mon = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  })
  return `${mon} '${String(y).slice(2)}`
}

/**
 * DataTrendChart — multi-series line chart as inline SVG. Shares one x-axis
 * (the sorted union of every series' months) and an auto-fitted y-axis with a
 * light gridline per tick. Draws an optional dashed baseline (index = 100) and
 * a labelled end dot per series. Responsive: the SVG scales to its container
 * width via a viewBox; a legend renders below as accessible HTML.
 */
export function DataTrendChart({
  series,
  title,
  caption,
  alt,
  meta,
  baseline,
  yFormat = (n) => n.toFixed(2),
  downloadSlug,
  anchorId,
}: DataTrendChartProps) {
  const W = 720
  const H = 320
  const M = { top: 18, right: 58, bottom: 34, left: 46 }
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom

  // Shared x axis: sorted union of all months across series.
  const monthSet = new Set<string>()
  for (const s of series) for (const p of s.points) monthSet.add(p.month)
  const months = [...monthSet].sort((a, b) => a.localeCompare(b))
  const xAt = (month: string) => {
    const i = months.indexOf(month)
    if (months.length <= 1) return M.left + plotW / 2
    return M.left + (i / (months.length - 1)) * plotW
  }

  // y domain from all values (+ baseline), padded to a tidy band.
  const values: number[] = []
  for (const s of series) for (const p of s.points) values.push(p.index)
  if (baseline !== undefined) values.push(baseline)
  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)
  const pad = Math.max((rawMax - rawMin) * 0.15, 0.5)
  const yMin = Math.floor(rawMin - pad)
  const yMax = Math.ceil(rawMax + pad)
  const yAt = (v: number) =>
    M.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH

  const tickCount = 4
  const yTicks = Array.from(
    { length: tickCount + 1 },
    (_, i) => yMin + ((yMax - yMin) / tickCount) * i
  )

  // Thin the x ticks to ~6 evenly-spaced labels so months never crowd.
  const xTickStep = Math.max(1, Math.ceil(months.length / 6))
  const xTickMonths = months.filter(
    (_, i) => i % xTickStep === 0 || i === months.length - 1
  )

  return (
    <FigureFrame
      title={title}
      caption={caption}
      meta={meta}
      downloadSlug={downloadSlug}
      anchorId={anchorId}
    >
      <ChartHoverLayer>
      <div className="w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={alt}
          className="block h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-chart-interactive
        >
          <FridgeWordmark x={W - M.right} y={2} anchor="end" size={13} />
          {/* horizontal gridlines + y labels */}
          {yTicks.map((t, i) => (
            <g key={`y${i}`} aria-hidden="true">
              <line
                x1={M.left}
                x2={W - M.right}
                y1={yAt(t)}
                y2={yAt(t)}
                stroke="#eef1f4"
                strokeWidth={1}
              />
              <text
                x={M.left - 8}
                y={yAt(t)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill="#9ca3af"
              >
                {yFormat(t)}
              </text>
            </g>
          ))}

          {/* baseline (index = 100) */}
          {baseline !== undefined && (
            <g aria-hidden="true">
              <line
                x1={M.left}
                x2={W - M.right}
                y1={yAt(baseline)}
                y2={yAt(baseline)}
                stroke="#9ca3af"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
              <text
                x={W - M.right + 6}
                y={yAt(baseline)}
                dominantBaseline="middle"
                fontSize={10}
                fontWeight={700}
                fill="#6b7280"
              >
                {yFormat(baseline)}
              </text>
            </g>
          )}

          {/* x labels */}
          {xTickMonths.map((mo) => (
            <text
              key={`x${mo}`}
              x={xAt(mo)}
              y={H - M.bottom + 20}
              textAnchor="middle"
              fontSize={11}
              fill="#9ca3af"
              aria-hidden="true"
            >
              {shortMonth(mo)}
            </text>
          ))}

          {/* series polylines + end dots */}
          {series.map((s) => {
            const pts = [...s.points].sort((a, b) => a.month.localeCompare(b.month))
            const d = pts.map((p) => `${xAt(p.month)},${yAt(p.index)}`).join(' ')
            const last = pts[pts.length - 1]
            return (
              <g key={s.label}>
                <polyline
                  points={d}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {/* Per-point hover targets — native tooltip + a dot that grows
                    on hover (interactive charts, item 14). */}
                {/* FRIDGE-450: every point is a first-class interactive mark —
                    data-pt feeds the INSTANT ChartHoverLayer tooltip (native
                    <title> stays as the no-JS fallback), tabIndex + aria-label
                    give keyboard and screen-reader parity with zero client JS. */}
                {pts.map((p) => (
                  <g key={`${s.label}-${p.month}`} className="cursor-pointer">
                    <circle cx={xAt(p.month)} cy={yAt(p.index)} r={2} fill={s.color} />
                    <circle
                      cx={xAt(p.month)}
                      cy={yAt(p.index)}
                      r={10}
                      fill={s.color}
                      className="opacity-0 hover:opacity-20 focus:opacity-30 transition-opacity"
                      data-pt
                      data-l={`${s.label} · ${shortMonth(p.month)}`}
                      data-v={yFormat(p.index)}
                      tabIndex={0}
                      aria-label={`${s.label}, ${shortMonth(p.month)}: ${yFormat(p.index)}`}
                    >
                      <title>{`${s.label} · ${shortMonth(p.month)}: ${yFormat(p.index)}`}</title>
                    </circle>
                  </g>
                ))}
                {last && (
                  <>
                    <circle cx={xAt(last.month)} cy={yAt(last.index)} r={3.5} fill={s.color} />
                    <text
                      x={xAt(last.month) + 7}
                      y={yAt(last.index)}
                      dominantBaseline="middle"
                      fontSize={10}
                      fontWeight={700}
                      fill={s.color}
                    >
                      {yFormat(last.index)}
                    </text>
                  </>
                )}
              </g>
            )
          })}
        </svg>
      </div>
      </ChartHoverLayer>

      {/* legend (accessible HTML, not baked into the SVG) */}
      {series.length > 1 && (
        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 list-none p-0 m-0">
          {series.map((s) => (
            <li key={s.label} className="inline-flex items-center gap-2 text-[12px] text-gray-700">
              <span
                className="inline-block w-3.5 h-[3px] rounded-full"
                style={{ backgroundColor: s.color }}
                aria-hidden="true"
              />
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </FigureFrame>
  )
}

// ── Bar chart ──────────────────────────────────────────────────────────────

export interface BarDatum {
  label: string
  value: number
  /** Optional per-bar color; defaults to Fridge blue. */
  color?: string
  /** FRIDGE-450: when the bar's subject has a live page (brand, category,
   * state…), its whole row becomes a real SVG <a> — native click-through,
   * crawlable, keyboard-reachable with zero client JS. */
  href?: string
}

interface DataBarChartProps {
  bars: BarDatum[]
  title: string
  caption: ReactNode
  alt: string
  meta?: string
  /** Format a value for the bar-end label. Default: raw number. */
  valueFormat?: (n: number) => string
  /** When set, adds the Download-PNG / Share toolbar (fridge-com-<slug>.png). */
  downloadSlug?: string
  /** Section anchor id for the shareable deep link. */
  anchorId?: string
  /** Opt in to a readable chart width inside a horizontal scroll container. */
  minWidth?: number
  /** Explicit font stack retained when the SVG is exported as an image. */
  svgFontFamily?: string
  /** Unit retained inside the SVG so PNG exports remain self-explanatory. */
  unitLabel?: string
}

/**
 * DataBarChart — horizontal bar chart as inline SVG. A fixed left gutter holds
 * category labels, bars extend proportional to the max value, and each value is
 * printed at the bar end. Reads cleanly for many long category names where a
 * vertical/rotated-label chart would not.
 */
export function DataBarChart({
  bars,
  title,
  caption,
  alt,
  meta,
  valueFormat = (n) => String(n),
  downloadSlug,
  anchorId,
  minWidth,
  svgFontFamily,
  unitLabel,
}: DataBarChartProps) {
  const W = 760
  const rowH = 30
  const barH = 16
  const top = 8
  const bottom = 8
  const footer = 22 // room for the embedded wordmark under the last bar
  const gutter = 176 // label column
  const valueW = 66 // room for the value at the bar end
  const trackX = gutter + 8
  const trackW = W - trackX - valueW
  const H = top + bottom + bars.length * rowH + footer
  const maxVal = Math.max(...bars.map((b) => b.value), 1)

  return (
    <FigureFrame
      title={title}
      caption={caption}
      meta={meta}
      downloadSlug={downloadSlug}
      anchorId={anchorId}
    >
      <ChartHoverLayer>
      <div
        className={minWidth ? "w-full overflow-x-auto" : "w-full overflow-hidden"}
        // A figure with a minWidth scrolls on narrow viewports and holds no
        // focusable descendant, so the scrollport itself must be focusable
        // (axe `scrollable-region-focusable`, serious — the rendered gate in
        // scripts/accessibility-axe-gate.ts). Same reconciliation as
        // app/data/_components/series-tables.tsx (FRIDGE-412): the rendered
        // gate wins; the static rule is suppressed on this line only.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- axe scrollable-region-focusable; see series-tables.tsx
        tabIndex={minWidth ? 0 : undefined}
        role={minWidth ? "region" : undefined}
        aria-label={minWidth ? `${title} — scroll chart` : undefined}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={minWidth ? { minWidth } : undefined}
          fontFamily={svgFontFamily}
          role="img"
          aria-label={alt}
          className="block h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-chart-interactive
        >
          {unitLabel && <text x={0} y={H - 7} fontSize={11} fill="#374151">{unitLabel}</text>}
          <FridgeWordmark x={W - valueW} y={H - footer + 4} anchor="end" size={13} />
          {bars.map((b, i) => {
            const y = top + i * rowH
            const cy = y + rowH / 2
            const w = Math.max((b.value / maxVal) * trackW, 2)
            const color = b.color ?? '#0469f0'
            // FRIDGE-450: a bar whose subject has a page is a REAL link (SVG
            // <a>, server-rendered); every bar is an instant-tooltip mark with
            // keyboard/screen-reader parity via tabIndex + aria-label.
            const Row: any = b.href ? 'a' : 'g'
            const rowProps = b.href
              ? { href: b.href, 'aria-label': `${b.label}: ${valueFormat(b.value)} — view page` }
              : { 'aria-label': `${b.label}: ${valueFormat(b.value)}`, tabIndex: 0 }
            return (
              <Row
                key={b.label}
                className="cursor-pointer"
                data-pt
                data-l={b.label}
                data-v={valueFormat(b.value)}
                {...rowProps}
              >
                <title>{`${b.label}: ${valueFormat(b.value)}`}</title>
                {/* category label */}
                <text
                  x={gutter}
                  y={cy}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={12}
                  fill="#374151"
                >
                  {b.label}
                </text>
                {/* track */}
                <rect
                  x={trackX}
                  y={cy - barH / 2}
                  width={trackW}
                  height={barH}
                  rx={3}
                  fill="#f1f4f8"
                  aria-hidden="true"
                />
                {/* fill */}
                <rect
                  x={trackX}
                  y={cy - barH / 2}
                  width={w}
                  height={barH}
                  rx={3}
                  fill={color}
                  className="hover:opacity-80 transition-opacity"
                />
                {/* value */}
                <text
                  x={trackX + w + 6}
                  y={cy}
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={700}
                  fill="#111827"
                >
                  {valueFormat(b.value)}
                </text>
              </Row>
            )
          })}
        </svg>
      </div>
      </ChartHoverLayer>
    </FigureFrame>
  )
}

// ── Spec-table figure ──────────────────────────────────────────────────────

export interface SpecRow {
  label: string
  value: string
  note?: string
}

interface SpecTableFigureProps {
  rows: SpecRow[]
  title: string
  caption: ReactNode
  alt: string
  meta?: string
  /** 1 or 2 columns of spec rows. Default 2. */
  columns?: 1 | 2
}

/**
 * SpecTableFigure — a styled specification table presented as owned imagery: a
 * branded card of label / value (/ note) rows in one or two columns, with a
 * caption. Semantic HTML (a definition list), so the values are fully readable
 * to assistive tech and crawlers; no SVG needed for a table.
 */
export function SpecTableFigure({
  rows,
  title,
  caption,
  alt,
  meta,
  columns = 2,
}: SpecTableFigureProps) {
  return (
    <FigureFrame title={title} caption={caption} meta={meta}>
      <dl
        className={`grid gap-x-10 gap-y-0 ${columns === 2 ? 'sm:grid-cols-2' : ''}`}
        aria-label={alt}
      >
        {rows.map((r) => (
          <div key={r.label} className="border-b border-gray-100 py-3">
            <dt className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500">
              {r.label}
            </dt>
            <dd className="mt-1 m-0">
              <span className="text-sm font-bold text-gray-900 tabular-nums">{r.value}</span>
              {r.note && (
                <span className="block mt-0.5 text-[12.5px] text-gray-600 leading-relaxed">
                  {r.note}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </FigureFrame>
  )
}

// ── Shared text helpers ─────────────────────────────────────────────────────

/** Greedy word-wrap into lines of at most `max` characters. Deterministic, so
 *  server and client render byte-identical <tspan> runs (no hydration drift). */
function wrapText(text: string, max: number): string[] {
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let cur = ''
  for (const w of words) {
    if (!cur) cur = w
    else if ((cur + ' ' + w).length <= max) cur += ' ' + w
    else {
      lines.push(cur)
      cur = w
    }
  }
  if (cur) lines.push(cur)
  return lines
}

// ── Small-multiples sparkline grid ──────────────────────────────────────────

export interface SmallMultiplePanelInput {
  label: string
  points: Array<{ month: string; index: number }>
  latestIndex: number
  latestMonth: string
  /** Distinguishes the aggregate rollups (drawn in orange) from primary types. */
  isRollup: boolean
}

interface SmallMultiplesGridProps {
  panels: SmallMultiplePanelInput[]
  /** Shared [min, max] index band so every panel is directly comparable. */
  domain: [number, number]
  /** Reference line drawn on every panel (the base month, = 100). */
  baseline: number
  title: string
  caption: ReactNode
  alt: string
  meta?: string
  yFormat?: (n: number) => string
  downloadSlug?: string
  anchorId?: string
}

/**
 * SmallMultiplesGrid — one inline-SVG grid of mini index-trend sparklines, a
 * panel per published category. Every panel shares ONE y-scale (the `domain`)
 * so the shapes are directly comparable, draws the base = 100 line as a dashed
 * reference, and prints the latest reading. Rollups render in Fridge orange,
 * primary types in Fridge blue. A single <svg> (so the Download-PNG action
 * captures the whole grid), with the real Fridge.com wordmark baked in.
 */
export function SmallMultiplesGrid({
  panels,
  domain,
  baseline,
  title,
  caption,
  alt,
  meta,
  yFormat = (n) => n.toFixed(1),
  downloadSlug,
  anchorId,
}: SmallMultiplesGridProps) {
  const cols = panels.length <= 4 ? panels.length : panels.length <= 9 ? 3 : 4
  const rows = Math.ceil(panels.length / cols)
  const W = 720
  const padX = 4
  const top = 24 // room for the wordmark above the grid
  const cellW = (W - padX * 2) / cols
  const cellH = 108
  const H = top + rows * cellH + 8

  const [yMin, yMax] = domain
  const span = yMax - yMin || 1

  return (
    <FigureFrame
      title={title}
      caption={caption}
      meta={meta}
      downloadSlug={downloadSlug}
      anchorId={anchorId}
    >
      <ChartHoverLayer>
      <div className="w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={alt}
          className="block h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-chart-interactive
        >
          <FridgeWordmark x={W - padX} y={2} anchor="end" size={13} />
          {panels.map((p, i) => {
            const col = i % cols
            const row = Math.floor(i / cols)
            const ox = padX + col * cellW
            const oy = top + row * cellH
            const plotL = ox + 8
            const plotR = ox + cellW - 10
            const plotT = oy + 22
            const plotB = oy + cellH - 16
            const color = p.isRollup ? '#ff9951' : '#0469f0'
            const yAt = (v: number) => plotB - ((v - yMin) / span) * (plotB - plotT)
            const n = p.points.length
            const xAt = (idx: number) =>
              n <= 1 ? (plotL + plotR) / 2 : plotL + (idx / (n - 1)) * (plotR - plotL)
            const line = p.points.map((pt, idx) => `${xAt(idx)},${yAt(pt.index)}`).join(' ')
            const last = p.points[n - 1]
            const labelText = p.label.length > 20 ? p.label.slice(0, 19) + '…' : p.label
            return (
              <g key={p.label}>
                {/* panel card */}
                <rect
                  x={ox + 3}
                  y={oy + 3}
                  width={cellW - 6}
                  height={cellH - 6}
                  rx={8}
                  fill="#fbfcfd"
                  stroke="#eef1f4"
                  strokeWidth={1}
                />
                {/* category label */}
                <text x={ox + 12} y={oy + 17} fontSize={10.5} fontWeight={700} fill="#374151">
                  {labelText}
                </text>
                {/* latest value, right-aligned in the label row */}
                <text
                  x={ox + cellW - 12}
                  y={oy + 17}
                  textAnchor="end"
                  fontSize={10.5}
                  fontWeight={700}
                  fill={color}
                >
                  {yFormat(p.latestIndex)}
                </text>
                {/* base = 100 reference */}
                <line
                  x1={plotL}
                  x2={plotR}
                  y1={yAt(baseline)}
                  y2={yAt(baseline)}
                  stroke="#d5dbe2"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  aria-hidden="true"
                />
                {/* sparkline */}
                <polyline
                  points={line}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.75}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {/* per-point hover targets (native tooltip; invisible in raster) */}
                {p.points.map((pt, idx) => (
                  <circle
                    key={`${p.label}-${pt.month}`}
                    cx={xAt(idx)}
                    cy={yAt(pt.index)}
                    r={6}
                    fill={color}
                    className="opacity-0 hover:opacity-20 focus:opacity-30 transition-opacity cursor-pointer"
                    data-pt
                    data-l={`${p.label} · ${shortMonth(pt.month)}`}
                    data-v={yFormat(pt.index)}
                    tabIndex={0}
                    aria-label={`${p.label}, ${shortMonth(pt.month)}: ${yFormat(pt.index)}`}
                  >
                    <title>{`${p.label} · ${shortMonth(pt.month)}: ${yFormat(pt.index)}`}</title>
                  </circle>
                ))}
                {/* end dot */}
                {last && <circle cx={xAt(n - 1)} cy={yAt(last.index)} r={2.6} fill={color} />}
              </g>
            )
          })}
        </svg>
      </div>
      </ChartHoverLayer>

      {/* legend */}
      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 list-none p-0 m-0">
        <li className="inline-flex items-center gap-2 text-[12px] text-gray-700">
          <span className="inline-block w-3.5 h-[3px] rounded-full bg-[#ff9951]" aria-hidden="true" />
          Aggregate rollups
        </li>
        <li className="inline-flex items-center gap-2 text-[12px] text-gray-700">
          <span className="inline-block w-3.5 h-[3px] rounded-full bg-[#0469f0]" aria-hidden="true" />
          Primary categories
        </li>
        <li className="inline-flex items-center gap-2 text-[12px] text-gray-500">
          <span
            className="inline-block w-3.5 border-t border-dashed border-gray-400"
            aria-hidden="true"
          />
          Base month = {baseline}
        </li>
      </ul>
    </FigureFrame>
  )
}

// ── At-a-glance infographic panel ───────────────────────────────────────────

export interface InfographicStat {
  value: string
  label: string
}

export interface InfographicMover {
  /** FRIDGE-450: when the mover's subject has a live page, the whole tile is
   * a real SVG <a> — native click-through with zero client JS. */
  href?: string
  /** Small uppercase kicker, e.g. "Furthest above base". */
  kicker: string
  /** The subject, e.g. a category or type name. */
  title: string
  /** The headline value, e.g. "108.4" or "70.7 kWh/ft³". */
  value: string
  /** One supporting line under the title. */
  sub: string
  direction: 'up' | 'down'
  /** Accent color for the value + arrow. Defaults to Fridge blue. */
  color?: string
}

interface InfographicPanelProps {
  eyebrow: string
  headline: InfographicStat
  /** 3–4 supporting stats shown as a hairline-separated strip. */
  stats: InfographicStat[]
  /** Up to two mover cards. */
  movers: InfographicMover[]
  footer: string
  title: string
  caption: ReactNode
  alt: string
  meta?: string
  downloadSlug?: string
  anchorId?: string
  /** Opt in to a readable chart width inside a horizontal scroll container. */
  minWidth?: number
  /** Explicit font stack retained when the SVG is exported as an image. */
  svgFontFamily?: string
}

/**
 * InfographicPanel — a single branded, press-ready "at a glance" SVG figure:
 * an eyebrow + embedded Fridge.com wordmark, one hero stat, a strip of
 * supporting figures, and up to two "biggest mover" cards with directional
 * arrows. Everything is one <svg> so the Download-PNG action exports the whole
 * panel with the real logo baked in. Purely presentational — every number is
 * passed in already derived from the published dataset.
 */
export function InfographicPanel({
  eyebrow,
  headline,
  stats,
  movers,
  footer,
  title,
  caption,
  alt,
  meta,
  downloadSlug,
  anchorId,
  minWidth,
  svgFontFamily,
}: InfographicPanelProps) {
  const W = 720
  const H = 388
  const padX = 28
  const statL = padX
  const statR = W - padX
  const statW = (statR - statL) / stats.length
  const moverTop = 236
  const moverH = 116
  const moverGap = 16
  const moverW = movers.length > 0 ? (statR - statL - moverGap * (movers.length - 1)) / movers.length : 0
  // Two label lines fit above the y=188 hairline (y=150 and y=170); a 42-char
  // wrap pushed long labels to a third line that sat ON the divider. 72 chars
  // per line ≈ 590px at 14.5px — inside the 664px inner width, two lines max
  // for every current label.
  const headlineLines = wrapText(headline.label, 72)
  // Hero value auto-fit: 900-weight digits at 72px run ≈ 0.6em each, so a
  // long value ("33.13 × 30.38 × 69.9″" on the dimensions report) overruns
  // the 664px inner width and clips at the card edge. Scale the font down —
  // with an 8% estimate margin — whenever the natural run would not fit.
  const headlineAvail = W - padX * 2
  const headlineNatural = headline.value.length * 72 * 0.6
  const headlineFont =
    headlineNatural > headlineAvail
      ? Math.max(40, Math.floor(72 * (headlineAvail / (headlineNatural * 1.08))))
      : 72

  return (
    <FigureFrame
      title={title}
      caption={caption}
      meta={meta}
      downloadSlug={downloadSlug}
      anchorId={anchorId}
    >
      <ChartHoverLayer>
      <div
        className={minWidth ? "w-full overflow-x-auto" : "w-full overflow-hidden"}
        // A figure with a minWidth scrolls on narrow viewports and holds no
        // focusable descendant, so the scrollport itself must be focusable
        // (axe `scrollable-region-focusable`, serious — the rendered gate in
        // scripts/accessibility-axe-gate.ts). Same reconciliation as
        // app/data/_components/series-tables.tsx (FRIDGE-412): the rendered
        // gate wins; the static rule is suppressed on this line only.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- axe scrollable-region-focusable; see series-tables.tsx
        tabIndex={minWidth ? 0 : undefined}
        role={minWidth ? "region" : undefined}
        aria-label={minWidth ? `${title} — scroll chart` : undefined}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={minWidth ? { minWidth } : undefined}
          fontFamily={svgFontFamily}
          role="img"
          aria-label={alt}
          className="block h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-chart-interactive
        >
          {/* top brand rule */}
          <defs>
            <linearGradient id="fridge-info-bar" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#0469f0" />
              <stop offset="1" stopColor="#ff9951" />
            </linearGradient>
          </defs>
          <rect x={0} y={0} width={W} height={5} fill="url(#fridge-info-bar)" />

          {/* header: eyebrow + real wordmark */}
          <text
            x={padX}
            y={30}
            fontSize={12}
            fontWeight={800}
            letterSpacing="1.6"
            fill="#0469f0"
          >
            {eyebrow.toUpperCase()}
          </text>
          <FridgeWordmark x={W - padX} y={18} anchor="end" size={16} />

          {/* hero stat */}
          <text
            x={padX}
            y={118}
            fontSize={headlineFont}
            fontWeight={900}
            letterSpacing="-2"
            fill="#0469f0"
          >
            {headline.value}
          </text>
          {headlineLines.map((ln, i) => (
            <text
              key={i}
              x={padX + 4}
              y={150 + i * 20}
              fontSize={14.5}
              fontWeight={600}
              fill="#5b6b7f"
            >
              {ln}
            </text>
          ))}

          {/* supporting stat strip */}
          <line x1={statL} x2={statR} y1={188} y2={188} stroke="#e6eaf0" strokeWidth={1} />
          {stats.map((s, i) => {
            const x = statL + i * statW
            return (
              <g key={s.label}>
                {i > 0 && (
                  <line x1={x} x2={x} y1={198} y2={224} stroke="#e6eaf0" strokeWidth={1} />
                )}
                <text x={x + 14} y={212} fontSize={19} fontWeight={900} fill="#0b1220">
                  {s.value}
                </text>
                <text x={x + 14} y={224} fontSize={10.5} fontWeight={600} fill="#6b7a8d">
                  {s.label}
                </text>
              </g>
            )
          })}

          {/* mover cards */}
          {movers.map((m, i) => {
            const x = statL + i * (moverW + moverGap)
            const c = m.color ?? '#0469f0'
            const subLines = wrapText(m.sub, 46)
            const ax = x + moverW - 30
            const ay = moverTop + 34
            // FRIDGE-450: a mover whose subject has a page is a real SVG <a>;
            // every mover is a keyboard-reachable, instant-tooltip mark.
            const Tile: any = m.href ? 'a' : 'g'
            const tileProps = m.href
              ? { href: m.href, 'aria-label': `${m.kicker}: ${m.title}, ${m.value} — view page` }
              : { 'aria-label': `${m.kicker}: ${m.title}, ${m.value}`, tabIndex: 0 }
            return (
              <Tile
                key={m.kicker}
                data-pt
                data-l={`${m.kicker} — ${m.title}`}
                data-v={m.value}
                className="cursor-pointer"
                {...tileProps}
              >
                <rect
                  x={x}
                  y={moverTop}
                  width={moverW}
                  height={moverH}
                  rx={12}
                  fill={`${c}0d`}
                  stroke={`${c}33`}
                  strokeWidth={1}
                />
                <text
                  x={x + 18}
                  y={moverTop + 24}
                  fontSize={10}
                  fontWeight={800}
                  letterSpacing="1.2"
                  fill={c}
                >
                  {m.kicker.toUpperCase()}
                </text>
                {/* direction arrow */}
                <path
                  d={
                    m.direction === 'up'
                      ? `M${ax} ${ay + 9} L${ax + 9} ${ay - 6} L${ax + 18} ${ay + 9} Z`
                      : `M${ax} ${ay - 6} L${ax + 18} ${ay - 6} L${ax + 9} ${ay + 9} Z`
                  }
                  fill={c}
                />
                <text x={x + 18} y={moverTop + 52} fontSize={26} fontWeight={900} fill={c}>
                  {m.value}
                </text>
                <text x={x + 18} y={moverTop + 74} fontSize={13.5} fontWeight={800} fill="#0b1220">
                  {m.title}
                </text>
                {subLines.map((ln, j) => (
                  <text
                    key={j}
                    x={x + 18}
                    y={moverTop + 90 + j * 14}
                    fontSize={11}
                    fill="#5b6b7f"
                  >
                    {ln}
                  </text>
                ))}
              </Tile>
            )
          })}

          {/* footer */}
          <text x={padX} y={H - 12} fontSize={11} fontWeight={600} fill="#8a97a6">
            {footer}
          </text>
        </svg>
      </div>
      </ChartHoverLayer>
    </FigureFrame>
  )
}

// ── Methodology flow diagram ────────────────────────────────────────────────

export interface FlowStage {
  n: string
  title: string
  body: string
}

interface MethodologyFlowDiagramProps {
  stages: FlowStage[]
  title: string
  caption: ReactNode
  alt: string
  meta?: string
  downloadSlug?: string
  anchorId?: string
}

/**
 * MethodologyFlowDiagram — a horizontal SVG flow of the matched-model method:
 * numbered stage cards joined by arrows, each carrying an honest one-line
 * description drawn from the methodology copy. One <svg> with the real
 * Fridge.com wordmark, exportable as a PNG.
 */
export function MethodologyFlowDiagram({
  stages,
  title,
  caption,
  alt,
  meta,
  downloadSlug,
  anchorId,
}: MethodologyFlowDiagramProps) {
  const W = 720
  const top = 26
  const padX = 6
  const gap = 18
  const n = stages.length
  const cardW = (W - padX * 2 - gap * (n - 1)) / n
  // Tall enough for the worst case: a two-line title plus a six-line body, so no
  // description overflows its card.
  const cardH = 184
  const H = top + cardH + 14

  return (
    <FigureFrame
      title={title}
      caption={caption}
      meta={meta}
      downloadSlug={downloadSlug}
      anchorId={anchorId}
    >
      <ChartHoverLayer>
      <div className="w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={alt}
          className="block h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-chart-interactive
        >
          <FridgeWordmark x={W - padX} y={2} anchor="end" size={13} />
          {stages.map((s, i) => {
            const x = padX + i * (cardW + gap)
            const y = top
            const titleLines = wrapText(s.title, 18)
            const bodyLines = wrapText(s.body, 26)
            const isThreshold = /threshold|n\s*≥|≥\s*30/i.test(s.title + s.body)
            const accent = isThreshold ? '#ff9951' : '#0469f0'
            return (
              <g
                key={s.n}
                data-pt
                data-l={`Step ${s.n} — ${s.title}`}
                data-v={s.body}
                tabIndex={0}
                aria-label={`Step ${s.n}: ${s.title}. ${s.body}`}
              >
                <rect
                  x={x}
                  y={y}
                  width={cardW}
                  height={cardH}
                  rx={12}
                  fill="#ffffff"
                  stroke="#e6eaf0"
                  strokeWidth={1.5}
                />
                {/* number badge */}
                <circle cx={x + 22} cy={y + 24} r={13} fill={accent} />
                <text
                  x={x + 22}
                  y={y + 24}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={12}
                  fontWeight={900}
                  fill="#ffffff"
                >
                  {s.n}
                </text>
                {/* title */}
                {titleLines.map((ln, j) => (
                  <text
                    key={j}
                    x={x + 16}
                    y={y + 58 + j * 16}
                    fontSize={13}
                    fontWeight={800}
                    fill="#0b1220"
                  >
                    {ln}
                  </text>
                ))}
                {/* body */}
                {bodyLines.map((ln, j) => (
                  <text
                    key={j}
                    x={x + 16}
                    y={y + 58 + titleLines.length * 16 + 8 + j * 13}
                    fontSize={10.5}
                    fill="#5b6b7f"
                  >
                    {ln}
                  </text>
                ))}
                {/* connector arrow to next card */}
                {i < n - 1 && (
                  <g aria-hidden="true">
                    <line
                      x1={x + cardW + 3}
                      x2={x + cardW + gap - 3}
                      y1={y + cardH / 2}
                      y2={y + cardH / 2}
                      stroke="#c3ccd8"
                      strokeWidth={2}
                    />
                    <path
                      d={`M${x + cardW + gap - 6} ${y + cardH / 2 - 4} L${x + cardW + gap - 1} ${y + cardH / 2} L${x + cardW + gap - 6} ${y + cardH / 2 + 4} Z`}
                      fill="#c3ccd8"
                    />
                  </g>
                )}
              </g>
            )
          })}
        </svg>
      </div>
      </ChartHoverLayer>
    </FigureFrame>
  )
}
